import React, { Fragment, useEffect, } from 'react';
import { Button, } from 'reactstrap';
import { useToggle } from 'react-use';
import { keyBy, get, orderBy, chunk, isEmpty, omit, sumBy, groupBy, } from 'lodash';
import numeral from 'numeral';
import { useBoolean } from 'react-use';
import fileDownload from 'js-file-download';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import qs from 'qs';
import { format as formatDate, addMilliseconds, } from 'date-fns';
import { toast } from 'react-toastify';

import env from '../../env';
import firebase, { functions } from '../../firebase';
import { idWithTenant, } from '../../shared/util';
import { boxSizes, } from '../../shared/config';
import { batch, getCollectionData, } from '../../shared/firebase';
import { computeInventory, contentTypes, } from '../../shared/models/product';
import { newId, createProductsWithSkuCheck } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import Page from '../hocs/Page';
import CaseFormModal from '../modals/CaseFormModal';
import ProductInventoryHistoryModal from '../modals/ProductInventoryHistoryModal';
import Field from '../Field';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ImportButton from '../ImportButton';
import QuerySortLabel from '../QuerySortLabel';
import QueryBoolean from '../QueryBoolean';
import DataToggle from '../DataToggle';
import ProductsTabs from '../ProductsTabs';
import ProductTagsDisplay from '../ProductTagsDisplay';

const { keys, values, entries } = Object;
const contentTypesByLabel = keyBy(entries(contentTypes).map(([k, v]) => ({ key: k, ...v })), 'label');
const db = firebase.firestore();
const sortIterateees = {
  id: _ => _.createdAt.toDate(),
};

export default Page(function Products (props) {
  const { user, tenant, history, } = props;
  const {
    showsAll,
    sort = { field: 'id', direction: 'desc', },
  } = useQueryParams();
  const [showsAmazonProductAddModal, toggleAmazonProductAddModal] = useBoolean(false);
  const productsRef = tenant.ref.collection('products');
  const receivingRequestsRef = tenant.ref.collection('receivingRequests');
  const shippingRequestsRef = tenant.ref.collection('shippingRequests');
  const receivingRequests = useCollectionSubscription(receivingRequestsRef.where('status', '!=', 'received'), [tenant.id]);
  const shippingRequests = useCollectionSubscription(shippingRequestsRef.where('status', 'not-in', ['shipped', 'discarded', 'cancelled']).orderBy('status'), [tenant.id]);
  const receivingRequestItemsGroupedByProductId = groupBy(receivingRequests.map(_ => _.items || []).flat(), 'productId');
  const shippingRequestItemsGroupedByProductId = groupBy(shippingRequests.map(_ => _.items || []).flat(), 'productId');
  const { items: productChunks, isLoading: isLoadingProductChunks } = useCollectionSubscription(tenant.ref.collection('productChunks'), [tenant.id], { detail: true });
  const chunkProducts = productChunks.flatMap(_ => entries(omit(_, ['id', 'ref', 'exists'])).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('products').doc(k), ...v, })));
  const { items: products, isLoading } = useCollectionSubscription(productsRef.where('isCase', '==', true), [tenant.id], { detail: true });
  const extraSellers = useCollectionSubscription(tenant.ref.collection('extraSellers').orderBy('createdAt'), [tenant.id]);
  const extraSellersById = keyBy(extraSellers, 'id');
  const chunkProductsBySku = keyBy(chunkProducts, 'sku');
  const productsBySku = keyBy(products, 'sku');
  const productItems = products.map((product) => {
    const { id } = product;
    const receivingRequestItems = receivingRequestItemsGroupedByProductId[id] || [];
    const receivingCount = sumBy(receivingRequestItems, 'quantity')
    const { goodInventory, ngInventory, waitingToShippingGoodCount, waitingToShippingNgCount, goodInventoryWithoutAllocation, ngInventoryWithoutAllocation } = computeInventory(product, shippingRequests);
    return {
      ...product,
      receivingRequestItems,
      receivingCount,
      goodInventory,
      ngInventory,
      waitingToShippingGoodCount,
      waitingToShippingNgCount,
      goodInventoryWithoutAllocation,
      ngInventoryWithoutAllocation,
    };
  });
  const sortedProductItems = orderBy(productItems, (sortIterateees[sort.field] || sort.field), sort.direction);
  const [lastProduct] = orderBy(chunkProducts, _ => parseInt(_.id.replace(/\D/g, ''), 10), 'desc');
  const newRef = !isLoadingProductChunks && productsRef.doc(newId(lastProduct));
  const processValues = _ => ({ ..._, isCase: true, isAmazonProduct: _.contentType === 'amazon', itemName: _.itemName || _.name.slice(0, 10), shippingSize: _.shippingSize || null, extraSellerId: _.extraSellerId || null, });
  const onClickDownloadSampleCsv = () => {
    const data = [
      {
        SKU: 'sku_case_123456',
        商品名: '本100冊と50冊のケース',
        内容種別: '通常商品',
        JANまたはバーコード情報: '1234123412345',
        品名: '書籍',
        内容商品SKU: 'sku_1',
        内容商品数量: 100,
      },
      {
        SKU: 'sku_case_123456',
        商品名: '',
        内容種別: '',
        JANまたはバーコード情報: '',
        品名: '',
        内容商品SKU: 'sku_2',
        内容商品数量: 50,
      },
    ];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, 'ケース_サンプル.csv');
  };
  useEffect(() => {
    user.ref.set({ preferences: { productsTab: '/products', } }, { merge: true });
  }, []);
  const processRows = async (rows) => {
    const validatedRows = await Promise.all(rows.map(validate.bind(null, rows)));
    if(validatedRows.filter(_ => !isEmpty(_.errors)).length > 0) {
      const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
      const content = encoder.encode(unparseCsv(validatedRows.map((row) => {
        return {
          ...row.source,
          エラー内容: row.errors.join('\n'),
        };
      })));
      fileDownload(content, 'エラー結果.csv');
      throw new Error();
    } else {
      const groupedBySkuEntries = Object.values(groupBy(validatedRows, 'sku'));
      const rowsForCreate = groupedBySkuEntries.map((group, i) => {
        const [row] = group;
        return {
          productId: (parseInt(newId(lastProduct), 10) + i).toString(),
          sku: row.sku,
          data: {
            ...omit(row, ['source', 'errors']),
            extraSellerId: null,
            itemName: row.itemName || row.name.slice(0, 10),
            goodInventory: 0,
            ngInventory: 0,
            isAmazonProduct: row.contentType === 'amazon',
            shippingSize: null,
            isCase: true,
            items: group.map(_ => ({ productId: chunkProductsBySku[_.itemSku].id, quantity: _.itemQuantity })),
            createdAt: addMilliseconds(new Date(), i),
          },
        };
      });
      await createProductsWithSkuCheck(db, productsRef, rowsForCreate);
    }
  };
  const validate = async (rows, row, i) => {
    const {
      商品名: name,
      SKU: sku,
      JANまたはバーコード情報: barcodeInfo,
      内容種別: contentTypeLabel,
      品名: itemName,
      内容商品SKU: itemSku,
      内容商品数量: itemQuantity,
    } = row;
    const contentType = contentTypesByLabel[contentTypeLabel]?.key;
    const otherSkus = chunkProducts.map(_ => _.sku);
    const isSubRow = rows.slice(0, i).map(_ => _.SKU).includes(sku);
    const errors = [
      (!isSubRow && !name) && '商品名は必須です',
      chunkProducts.includes(sku) && 'SKUが他に存在します',
      sku.match(/\s/) && 'SKUに空白やタブ等が含まれています',
      (!isSubRow && contentType == null) && '内容種別が正しくありません',
      (itemName && itemName.length > 16) && '品名は16文字以内です',
      chunkProductsBySku[itemSku] == null && '存在しない内容商品SKUです',
      !(itemQuantity > 0) && '内容商品数量は1以上で指定してください',
    ];
    return {
      source: row,
      errors: errors.filter(_ => _),
      name,
      sku,
      barcodeInfo,
      contentType,
      itemName,
      itemSku,
      itemQuantity,
    }
  };

  return (
    <div className="products container-fluid">
      <div className="p-4 bg-white my-4">
        <ProductsTabs activeTab="cases" products={products} shippingRequests={shippingRequests} tenant={tenant} />
        <div className="d-flex justify-content-start mb-1 gap-1 align-items-end">
          <Button onClick={onClickDownloadSampleCsv}>
            <span className="fas fa-download mr-1" />
            サンプルCSVダウンロード
          </Button>
          <ImportButton processRows={processRows} disabled={isLoadingProductChunks} />
          <AddButton
            itemRef={newRef}
            FormModal={CaseFormModal}
            processValues={processValues}
            disabled={isLoadingProductChunks}
            beforeSave={async ({ id, ...values }) => {
              const data = { ...omit(processValues({ ...values }), ['id', 'ref']), createdAt: new Date(), };
              await createProductsWithSkuCheck(db, productsRef, [{ productId: id, sku: values.sku, data }]);
              return { skipDefaultSet: true };
            }}
            formProps={{
              tenant,
              chunkProducts,
            }}
          />
          <div className="ml-2">
            <QueryBoolean paramName="showsAll" label="非表示も表示" defaultValue={'0'} />
          </div>
        </div>
        <div className="overflow-auto">
          {
            sortedProductItems.length > 0 ? (
              <table className="table table-sm table-bordered sticky-table">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ minWidth: 150 }}>
                      <QuerySortLabel field="sku">
                        SKU
                      </QuerySortLabel>
                      <br />
                      <QuerySortLabel field="id">
                        登録日
                      </QuerySortLabel>
                      <div className="small">商品ID</div>
                      {
                        tenant.tenantOptionValues?.includes('multiSellers') && (
                          <div>ショップ</div>
                        )
                      }
                    </th>
                    <th style={{ minWidth: 200 }}>
                      <QuerySortLabel field="name">
                        商品名
                      </QuerySortLabel>
                      <br />
                      品名
                      <br />
                      サイズ
                    </th>
                    <th style={{ minWidth: 200 }}>
                      JANまたはバーコード情報
                    </th>
                    <th style={{ minWidth: 60 }}>
                      <QuerySortLabel field="goodInventory">
                        在庫
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 90 }}>
                      <QuerySortLabel field="ngInventory">
                        販売不可在庫
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 180 }}>入出荷作業中</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    (showsAll === '1' ? sortedProductItems : sortedProductItems.filter(_ => !_.hides)).map((productItem) => {
                      const { id, ref, isAmazonProduct, name, sku, barcodeInfo, itemName, requestsNeko = false, goodInventory = 0, ngInventory = 0, fnsku, asin, receivingCount, waitingToShippingGoodCount, waitingToShippingNgCount, goodInventoryWithoutAllocation, ngInventoryWithoutAllocation, createdAt, shippingSize, contentType, extraSellerId, } = productItem;
                      const beforeWrite = async () => {
                        const [receivingRequest] = await getCollectionData(tenant.ref.collection('receivingRequests').where('productIds', 'array-contains', id).limit(1));
                        const [shippingRequest] = await getCollectionData(tenant.ref.collection('shippingRequests').where('productIds', 'array-contains', id).limit(1));
                        if(receivingRequest || shippingRequest) {
                          toast.error('一度でも入出荷があったケースは処理できません。');
                          return false;
                        }
                      };

                      return (
                        <tr key={id}>
                          <td>
                            <div>
                              <span className="font-weight-bold">SKU:</span> {sku}
                            </div>
                            <div>{formatDate(createdAt.toDate(), 'yyyy/MM/dd')}</div>
                            <div className="small">{idWithTenant(ref)}</div>
                            {tenant.tenantOptionValues?.includes('multiSellers') && contentType === 'amazon' && <div>{extraSellerId ? extraSellersById[extraSellerId]?.name : 'メイン'}</div>}
                            {requestsNeko && <div className="badge badge-warning">ポスト便希望</div>}
                            <ProductTagsDisplay product={productItem} productTags={[]} />
                            {user.dev && <a href={`https://console.firebase.google.com/u/0/project/${env('FIREBASE_PROJECT_ID')}/firestore/data/~2Ftenants~2F${tenant.id}~2Fproducts~2F${id}`} target="_blank">F</a>}
                          </td>
                          <td>
                            <div>
                              {name}
                            </div>
                            <div className="font-weight-bold">
                              {itemName}
                            </div>
                            <div className="font-weight-bold">
                              {boxSizes[shippingSize]?.label}
                            </div>
                          </td>
                          <td>
                            <div>
                              <span className="font-weight-bold">JAN:</span> {barcodeInfo}
                            </div>
                            {isAmazonProduct && <div className="font-weight-bold">ASIN: {asin}</div>}
                            {isAmazonProduct && <div className="font-weight-bold">FNSKU: {fnsku}</div>}
                          </td>
                          <td className="text-right">
                            {numeral(goodInventory).format('0,0')}
                          </td>
                          <td className="text-right">
                            {numeral(ngInventory).format('0,0')}
                          </td>
                          <td>
                            <table className="mb-0 table table-sm table-borderless">
                              <tbody>
                                <tr>
                                  <th>入荷依頼中</th>
                                  <td className="text-right">{numeral(receivingCount).format('0,0')}</td>
                                </tr>
                                <tr>
                                  <th>出荷依頼中</th>
                                  <td className="text-right">{numeral(waitingToShippingGoodCount).format('0,0')}</td>
                                </tr>
                                <tr>
                                  <th>返送・廃棄依頼中</th>
                                  <td className="text-right">{numeral(waitingToShippingNgCount).format('0,0')}</td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 text-nowrap">
                              <EditButton size="sm" processValues={processValues} itemRef={ref} FormModal={CaseFormModal} formProps={{ tenant, chunkProducts, values: productItem, isSubmitDisabled: [goodInventory, ngInventory, receivingCount, waitingToShippingGoodCount, waitingToShippingNgCount].some(_ => _ !== 0), }} beforeUpdate={beforeWrite} />
                              <DeleteButton size="sm" item={productItem} itemRef={ref} disabled={[goodInventory, ngInventory, receivingCount, waitingToShippingGoodCount, waitingToShippingNgCount].some(_ => _ !== 0)} beforeDelete={beforeWrite} />
                              <DataToggle data={productItem} fieldName="hides" label="非表示" inputProps={{ style: { transform: 'initial' }, }} labelProps={{ className: 'small', }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                ケースは未登録です
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
